import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "to", "href", "onClick", "use", "size"],
  _excluded2 = ["id", "endSlot", "completed", "duration", "remainingTime", "footer", "description", "thumbnailSrc", "thumbnailOverlay", "disabled", "name", "onClick", "to", "use", "size", "href"];
import { Link } from 'react-router-dom';
import styled, { css, theme } from '../../utils/Styling';
import UIImage from 'UIComponents/image/UIImage';
import { useState } from 'react';
import ACSkeletonBox from '../loading/ACSkeletonBox';
import { H5, H7 } from 'UIComponents/elements/headings';
import UITruncateString from 'UIComponents/text/UITruncateString';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ACIcon from '../icon/ACIcon';
import UIFlex from 'UIComponents/layout/UIFlex';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import I18n from 'I18n';
import ACDynamicThumbnailImage from './ACDynamicThumbnailImage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const linkStyles = css(["display:flex;min-height:var(--card-height);align-items:center;width:100%;border-radius:3px;gap:12px;padding-right:8px;&:hover{background-color:var(--hover-background-color);box-shadow:var(--hover-background-color) 0px 0px 0px 4px;}color:inherit;font:inherit;line-height:inherit;text-decoration:inherit;-webkit-font-smoothing:auto;:hover{color:inherit;font:inherit;line-height:inherit;text-decoration:inherit;}"]);
const InternalLink = styled(Link).withConfig({
  displayName: "ACInlineContentCard__InternalLink",
  componentId: "b3wmxq-0"
})(["--hover-background-color:", ";--card-height:", ";", ""], props => props.use === 'on-light' ? theme.colors.GYPSUM : theme.colors.PANTERA, props => props.size === 'small' ? '70px' : '100px', linkStyles);
const ExternalLink = styled.a.withConfig({
  displayName: "ACInlineContentCard__ExternalLink",
  componentId: "b3wmxq-1"
})(["--hover-background-color:", ";--card-height:", ";", ""], props => props.use === 'on-light' ? theme.colors.GYPSUM : theme.colors.PANTERA, props => props.size === 'small' ? '70px' : '100px', linkStyles);
const ClickableCard = styled.div.withConfig({
  displayName: "ACInlineContentCard__ClickableCard",
  componentId: "b3wmxq-2"
})(["--hover-background-color:", ";--card-height:", ";", ""], props => props.use === 'on-light' ? theme.colors.GYPSUM : theme.colors.PANTERA, props => props.size === 'small' ? '70px' : '100px', linkStyles);
const ThumbnailOverlayWrapper = styled.div.withConfig({
  displayName: "ACInlineContentCard__ThumbnailOverlayWrapper",
  componentId: "b3wmxq-3"
})(["position:absolute;height:100%;width:100%;", ""], ({
  disabled
}) => disabled && css(["&::after{content:'';display:block;position:absolute;top:0;right:0;bottom:0;left:0;background-color:", ";opacity:0.6;z-index:1;pointer-events:none;}"], theme.colors.FLINT));
const StartSlot = styled.div.withConfig({
  displayName: "ACInlineContentCard__StartSlot",
  componentId: "b3wmxq-4"
})(["position:relative;height:100%;width:var(--card-height);"]);
const ThumbnailWrapper = styled.div.withConfig({
  displayName: "ACInlineContentCard__ThumbnailWrapper",
  componentId: "b3wmxq-5"
})(["position:relative;border-radius:3px;overflow:hidden;min-width:var(--card-height);aspect-ratio:1 / 1;"]);
const MiddleSlot = styled.div.withConfig({
  displayName: "ACInlineContentCard__MiddleSlot",
  componentId: "b3wmxq-6"
})(["display:flex;justify-content:space-between;flex-direction:column;flex:1;", ""], props => props.size === 'medium' && 'gap: 8px;');
const EndSlot = styled.div.withConfig({
  displayName: "ACInlineContentCard__EndSlot",
  componentId: "b3wmxq-7"
})(["display:flex;align-items:center;"]);
const CardCompletedMessage = () => {
  return /*#__PURE__*/_jsxs(UIFlex, {
    align: "center",
    gap: 4,
    children: [/*#__PURE__*/_jsx(ACIcon, {
      color: theme.colors.CALYPSO,
      name: "checkmark"
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: `academyCommon.progress.completed`
    })]
  });
};
const ContentCardLink = _ref => {
  let {
      children,
      to,
      href,
      onClick = () => {},
      use = 'on-light',
      size = 'small'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (to) {
    return /*#__PURE__*/_jsx(InternalLink, Object.assign({
      onClick: onClick,
      size: size,
      to: to,
      use: use
    }, rest, {
      children: children
    }));
  }
  if (href) {
    return /*#__PURE__*/_jsx(ExternalLink, Object.assign({
      href: href,
      onClick: onClick,
      size: size,
      use: use
    }, rest, {
      children: children
    }));
  }
  return /*#__PURE__*/_jsx(ClickableCard, Object.assign({
    onClick: onClick,
    role: "button",
    size: size,
    use: use
  }, rest, {
    children: children
  }));
};
const ACInlineContentCard = _ref2 => {
  let {
      id,
      endSlot,
      completed,
      duration,
      remainingTime,
      footer,
      description,
      thumbnailSrc,
      thumbnailOverlay,
      disabled = false,
      name,
      onClick = () => {},
      to,
      use = 'on-light',
      size = 'small',
      href
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const progress = completed ? 1 : duration && remainingTime ? (duration - remainingTime) / duration : 0;
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const thumbnailLoading = thumbnailSrc && !thumbnailLoaded;
  return /*#__PURE__*/_jsxs(ContentCardLink, Object.assign({
    href: href,
    onClick: onClick,
    size: size,
    to: to,
    use: use
  }, props, {
    children: [/*#__PURE__*/_jsx(StartSlot, {
      children: /*#__PURE__*/_jsxs(ThumbnailWrapper, {
        children: [/*#__PURE__*/_jsx(ACSkeletonBox, {
          style: {
            position: 'absolute',
            height: '100%',
            width: '100%'
          }
        }), thumbnailSrc ? /*#__PURE__*/_jsx(UIImage, {
          alt: I18n.text('academyCommon.contentCard.thumbnailAlt', {
            title: name
          }),
          loading: "lazy",
          onLoad: () => setThumbnailLoaded(true),
          src: thumbnailSrc,
          style: {
            visibility: thumbnailLoading ? 'hidden' : 'visible',
            position: 'absolute',
            height: '100%'
          }
        }) : /*#__PURE__*/_jsx(ACDynamicThumbnailImage, {
          seed: id,
          style: {
            position: 'absolute',
            height: '100%'
          },
          title: name,
          type: "square"
        }), /*#__PURE__*/_jsxs(ThumbnailOverlayWrapper, {
          disabled: disabled,
          children: [thumbnailOverlay, /*#__PURE__*/_jsx("div", {
            style: {
              position: 'absolute',
              bottom: 0,
              width: '100%',
              height: 3
            },
            children: /*#__PURE__*/_jsx(UINanoProgress, {
              "aria-label": I18n.text('academyCommon.contentCard.progressBar'),
              color: progress === 1 ? theme.colors.CALYPSO : theme.colors.SORBET,
              height: 3,
              value: progress * 100
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsxs(MiddleSlot, {
      size: size,
      children: [size === 'small' ? /*#__PURE__*/_jsx(H7, {
        className: "m-bottom-1",
        children: name
      }) : /*#__PURE__*/_jsx(H5, {
        className: "m-bottom-1",
        children: name
      }), completed ? /*#__PURE__*/_jsx(CardCompletedMessage, {}) : description && /*#__PURE__*/_jsx(UITruncateString, {
        children: description
      }), footer]
    }), /*#__PURE__*/_jsx(EndSlot, {
      children: endSlot
    })]
  }));
};
export default ACInlineContentCard;